import type {
  SendMfaCodeProps,
  SendMfaCodeResponse,
  VerifyMfaCodeProps,
  VerifyMfaCodeResponse,
} from '@iheartjane/data-access';
import { trackError } from '@iheartjane/tracking';
import { useContextEnsuringProvider } from '@iheartjane/utils';
import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';

interface MfaContextType {
  canResendCode: (seconds?: number) => boolean;
  canSendCode: (seconds?: number) => boolean;
  codeResent: boolean;
  codeSent: boolean;
  errorSendingCode: string | null;
  errorVerifyingCode: string | null;
  getNewPhone: () => string | undefined;
  launch: (options?: {
    newPhone?: string;
    onCancel?: () => void;
    onSuccess?: () => void;
  }) => void;
  mfaCancel: () => void;
  open: boolean;
  requiresMfa: (minutes?: number) => boolean;
  resendCode: () => Promise<void>;
  sendCode: () => Promise<boolean>;
  sendingCode: boolean;
  setNewPhone: (phone: string | undefined) => void;
  verifyCode: (code: string) => Promise<boolean>;
  verifyingCode: boolean;
}

const MfaContext = createContext<MfaContextType | null>(null);
MfaContext.displayName = 'MFA';

export const useMfa = () => useContextEnsuringProvider(MfaContext);

// The SMS/email is dispatched server-side, but the HTTP response can lag or
// hang. Stop waiting after this window so the form is never blocked forever.
const SEND_CODE_TIMEOUT_MS = 15 * 1000;

const isMoreThanTime = (date: Date, seconds: number) => {
  const now = new Date();
  const differenceInSeconds = Math.floor(
    (now.getTime() - date.getTime()) / 1000
  );
  return differenceInSeconds > seconds;
};

export interface MfaProviderProps {
  children: ReactNode;
  getJwt: () => string | null;
  isKiosk?: boolean;
  onClose?: () => void;
  onOpen?: () => void;
  sendMfaCode: (props: SendMfaCodeProps) => Promise<SendMfaCodeResponse>;
  userId?: string | number | null;
  verifyMfaCode: (props: VerifyMfaCodeProps) => Promise<VerifyMfaCodeResponse>;
}

export const MfaProvider = ({
  children,
  getJwt,
  isKiosk = false,
  onClose,
  onOpen,
  sendMfaCode,
  userId,
  verifyMfaCode,
}: MfaProviderProps) => {
  // Coordination state (persists across flows)
  const lastMfaDate = useRef<Date | null>(null);
  const lastCodeSentDate = useRef<Date | null>(null);
  const lastCodeResentDate = useRef<Date | null>(null);

  // Launch state
  const [open, setOpen] = useState(false);
  const onCancelRef = useRef<(() => void) | null>(null);
  const onSuccessRef = useRef<(() => void) | null>(null);
  const newPhoneRef = useRef<string | null>(null);

  // Flow state (reset each launch)
  const [sendingCode, setSendingCode] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [codeResent, setCodeResent] = useState(false);
  const [verifyingCode, setVerifyingCode] = useState(false);
  const [errorSendingCode, setErrorSendingCode] = useState<string | null>(null);
  const [errorVerifyingCode, setErrorVerifyingCode] = useState<string | null>(
    null
  );

  const clearRefs = () => {
    onCancelRef.current = null;
    onSuccessRef.current = null;
    newPhoneRef.current = null;
  };

  const clearTimestamps = () => {
    lastMfaDate.current = null;
    lastCodeSentDate.current = null;
    lastCodeResentDate.current = null;
  };

  const resetFlowState = () => {
    setSendingCode(false);
    setCodeSent(false);
    setCodeResent(false);
    setVerifyingCode(false);
    setErrorSendingCode(null);
    setErrorVerifyingCode(null);
  };

  const openModal = () => {
    onOpen?.();
    setOpen(true);
  };

  const closeModal = () => {
    setOpen(false);
    onClose?.();
  };

  // Reset everything on user change
  useEffect(() => {
    clearRefs();
    clearTimestamps();
    resetFlowState();
    setOpen(false);
  }, [userId]);

  // Coordination methods
  const handleRequiresMfa = useCallback(
    (minutes = 60) =>
      !lastMfaDate.current || isMoreThanTime(lastMfaDate.current, minutes * 60),
    []
  );

  const handleCanSendCode = useCallback((seconds = 20) => {
    const lastSent = lastCodeSentDate.current;
    const lastResent = lastCodeResentDate.current;

    if (!lastSent || !!newPhoneRef.current) return true;
    if (!lastResent) return isMoreThanTime(lastSent, seconds);

    const mostRecent =
      lastSent.getTime() > lastResent.getTime() ? lastSent : lastResent;
    return isMoreThanTime(mostRecent, seconds);
  }, []);

  const handleCanResendCode = useCallback(
    (seconds = 20) =>
      !lastCodeResentDate.current ||
      isMoreThanTime(lastCodeResentDate.current, seconds),
    []
  );

  const handleGetNewPhone = useCallback(
    () => newPhoneRef.current || undefined,
    []
  );

  const handleSetNewPhone = useCallback((phone: string | undefined) => {
    newPhoneRef.current = phone || null;
  }, []);

  // Launch/cancel
  const handleLaunch = useCallback(
    (options?: {
      newPhone?: string;
      onCancel?: () => void;
      onSuccess?: () => void;
    }) => {
      resetFlowState();
      onCancelRef.current = options?.onCancel || null;
      onSuccessRef.current = options?.onSuccess || null;
      newPhoneRef.current = options?.newPhone || null;

      // Handle the case where a user cancelled a previous launch
      // and is re-launching the flow.
      if (lastCodeSentDate.current && !handleCanSendCode()) {
        setCodeSent(true);
      }

      openModal();
    },
    [handleCanSendCode, onOpen]
  );

  const handleCancel = useCallback(() => {
    onCancelRef.current?.();
    clearRefs();
    resetFlowState();
    closeModal();
  }, [onClose]);

  const handleMfaSuccess = useCallback(() => {
    lastMfaDate.current = new Date();
    onSuccessRef.current?.();
    clearRefs();
    resetFlowState();
    closeModal();
  }, [onClose]);

  // Flow actions
  const handleSendCode = useCallback(async (): Promise<boolean> => {
    setSendingCode(true);
    setErrorSendingCode(null);
    setErrorVerifyingCode(null);

    let success = false;
    let timeoutId: ReturnType<typeof setTimeout>;

    try {
      await Promise.race([
        sendMfaCode({
          jwt: getJwt(),
          newPhone: newPhoneRef.current || undefined,
          isKiosk,
        }),
        new Promise<never>((_, reject) => {
          timeoutId = setTimeout(
            () => reject(new Error('Request timed out. Please try again.')),
            SEND_CODE_TIMEOUT_MS
          );
        }),
      ]);
      setCodeSent(true);
      lastCodeSentDate.current = new Date();
      success = true;
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to send code';
      setErrorSendingCode(message);
      trackError(message);
    } finally {
      clearTimeout(timeoutId!);
    }

    setSendingCode(false);
    return success;
  }, [getJwt, isKiosk, sendMfaCode]);

  const handleResendCode = useCallback(async () => {
    const success = await handleSendCode();
    if (success) {
      setCodeResent(true);
      lastCodeResentDate.current = new Date();
    }
  }, [handleSendCode]);

  const handleVerifyCode = useCallback(
    async (code: string): Promise<boolean> => {
      setErrorVerifyingCode(null);
      setVerifyingCode(true);

      let success = false;

      try {
        const response = await verifyMfaCode({
          code,
          isKiosk,
          jwt: getJwt(),
        });

        if (response.success) {
          handleMfaSuccess();
          success = true;
        } else {
          setErrorVerifyingCode(
            response.error
              ? 'There was an error processing this request. Please try again.'
              : 'This code is incorrect, check your code and try again.'
          );
        }
      } catch {
        setErrorVerifyingCode('Something went wrong. Please try again.');
      }

      setVerifyingCode(false);
      return success;
    },
    [getJwt, isKiosk, handleMfaSuccess, verifyMfaCode]
  );

  const value = useMemo(
    () => ({
      mfaCancel: handleCancel,
      canResendCode: handleCanResendCode,
      canSendCode: handleCanSendCode,
      codeResent,
      codeSent,
      errorSendingCode,
      errorVerifyingCode,
      getNewPhone: handleGetNewPhone,
      launch: handleLaunch,
      open,
      requiresMfa: handleRequiresMfa,
      resendCode: handleResendCode,
      sendCode: handleSendCode,
      sendingCode,
      setNewPhone: handleSetNewPhone,
      verifyCode: handleVerifyCode,
      verifyingCode,
    }),
    [
      handleCancel,
      handleCanResendCode,
      handleCanSendCode,
      codeResent,
      codeSent,
      errorSendingCode,
      errorVerifyingCode,
      handleGetNewPhone,
      handleLaunch,
      open,
      handleRequiresMfa,
      handleResendCode,
      handleSendCode,
      sendingCode,
      handleSetNewPhone,
      handleVerifyCode,
      verifyingCode,
    ]
  );

  return <MfaContext.Provider value={value}>{children}</MfaContext.Provider>;
};
